import { browserAdaptor } from '@mathjax/src/mjs/adaptors/browserAdaptor';
import { MathDocument } from '@mathjax/src/mjs/core/MathDocument';
import { RegisterHTMLHandler } from '@mathjax/src/mjs/handlers/html';
import { MathML } from '@mathjax/src/mjs/input/mathml';
import { TeX } from '@mathjax/src/mjs/input/tex';
import '@mathjax/src/mjs/input/tex/ams/AmsConfiguration';
import '@mathjax/src/mjs/input/tex/base/BaseConfiguration';
import '@mathjax/src/mjs/input/tex/bbox/BboxConfiguration';
import '@mathjax/src/mjs/input/tex/boldsymbol/BoldsymbolConfiguration';
import '@mathjax/src/mjs/input/tex/color/ColorConfiguration';
import '@mathjax/src/mjs/input/tex/configmacros/ConfigMacrosConfiguration';
import '@mathjax/src/mjs/input/tex/enclose/EncloseConfiguration';
import '@mathjax/src/mjs/input/tex/mathtools/MathtoolsConfiguration';
import '@mathjax/src/mjs/input/tex/newcommand/NewcommandConfiguration';
import '@mathjax/src/mjs/input/tex/noundefined/NoUndefinedConfiguration';
import '@mathjax/src/mjs/input/tex/textmacros/TextMacrosConfiguration';
import '@mathjax/src/mjs/input/tex/unicode/UnicodeConfiguration';
import { mathjax } from '@mathjax/src/mjs/mathjax';
import { SVG } from '@mathjax/src/mjs/output/svg';
import { convertLatexToMathMl } from 'mathlive';
import { Mark, ProseMirrorNode, Schema } from 'prosemirror-model';

/** Matches a full `<math>...</math>` or `<mml:math>...</mml:math>` wrapper, used to detect
 *  when an equation field's saved value is raw MathML rather than LaTeX. */
export const wrappedMathRegex = /^<((?:mml:)?math)([^>]*)>([\s\S]*)<\/\1>$/i;

const newcmDynamicPrefix = '@mathjax/mathjax-newcm-font/js/svg/dynamic/';
const defaultAsyncLoad = mathjax.asyncLoad?.bind(mathjax);

// Override MathJax's asyncLoad to load the font files from our node_modules
mathjax.asyncLoad = (name: string) => {
  if (name.startsWith(newcmDynamicPrefix)) {
    const fileName = name.substring(newcmDynamicPrefix.length).replace(/\.js$/, '');

    return import(
      /* webpackInclude: /\.js$/ */
      /* webpackMode: "lazy" */
      /* webpackChunkName: "mjx-newcm-[request]" */
      `@mathjax/mathjax-newcm-font/mjs/svg/dynamic/${fileName}.js`
    );
  }

  if (defaultAsyncLoad) {
    return defaultAsyncLoad(name);
  }

  return Promise.reject(`Can't load '${name}': No fallback async loader configured`);
};

let initialized: boolean = false;
let mmlDocument: MathDocument<HTMLElement, Text, Document>;
let texDocument: MathDocument<HTMLElement, Text, Document>;

/**
 * Initializes MathJax with the desired configuration.
 * Initialization will only happen once, subsequent calls to this function will be no-ops.
 */
export function initMathJax() {
  if (initialized) {
    return;
  }

  const adaptor = browserAdaptor();
  RegisterHTMLHandler(adaptor);

  const texInput = new TeX({
    packages: ['base', 'ams', 'newcommand', 'noundefined', 'textmacros', 'enclose', 'unicode', 'mathtools', 'color', 'boldsymbol', 'bbox', 'configmacros'],
    // MathLive's Font Style > Bold command emits \bm{...} (not \boldsymbol{...}) whenever the
    // bolded selection isn't plain letters/digits — see emitBoldRun() in MathLive's own source.
    // MathJax has no 'bm' package (that's a separate, unshipped LaTeX package), only 'boldsymbol'
    // which defines \boldsymbol but not \bm, so alias it here rather than leave \bm undefined.
    macros: {
      bm: ['\\boldsymbol{#1}', 1],
      overgroup: ['\\overparen{#1}', 1],
      undergroup: ['\\underparen{#1}', 1],
      Colon: '\\dblcolon',
      doubleprime: '\\prime\\prime',
    }
  });

  const mathMlInput = new MathML();
  const svgOutput = new SVG({
    fontCache: 'local'
  });

  mmlDocument = mathjax.document(document, {
    InputJax: mathMlInput,
    OutputJax: svgOutput
  });
  texDocument = mathjax.document(document, {
    InputJax: texInput,
    OutputJax: svgOutput
  });

  // insert mathjax stylesheet
  const styleSheet = svgOutput.styleSheet(texDocument) as HTMLStyleElement;
  document.head.appendChild(styleSheet);

  initialized = true;
}

/**
 * MathLive's own \placeholder{} marks an intentionally-unfilled slot (e.g. an empty matrix
 * cell) — it has no LaTeX expansion (unlike \exponentialE/\imaginaryI, which 'latex-expanded'
 * handles) because there's nothing to expand it to. MathJax renders it as an <merror>, and that
 * error text is drawn at a fixed size that overflows/clips when nested inside something small
 * like a matrix cell, making it unreadable rather than just unstyled. Swap it for a plain empty
 * box glyph before rendering so the rest of the equation still renders normally.
 */
export function replacePlaceholders(equation: string): string {
  return equation.replace(/\\placeholder\s*\{[^{}]*\}/g, '\\square');
}

/**
 * MathML's element names (MathML Core / MathML 3), longest-first so a corrupted longer tag name is
 * preferred over a shorter one that happens to be one of its own prefixes (e.g. 'msub' should win
 * over 'ms' when the source was actually 'msubsup'). Four names are themselves a prefix of another
 * name in this list even after that ordering — 'ms' (of msub/msup/msubsup/msqrt/mstyle/mspace),
 * 'msub' (of msubsup), 'mo' (of mover), and 'munder' (of munderover) — each gets a negative
 * lookahead below excluding the specific next letter(s) that would mean it's actually the longer
 * tag, so an uncorrupted `<msub>`/`<mover>`/etc. (no attributes at all) is never misidentified and
 * split mid-name.
 */
const MATHML_TAG_ALTERNATION =
  'annotation-xml|mmultiscripts|malignmark|maligngroup|mlabeledtr|' +
  'munderover|msubsup|annotation|semantics|' +
  'mfenced|mphantom|mpadded|menclose|mfrac|mroot|mstyle|mspace|msqrt|' +
  'munder(?!o)|mover|msub(?!s)|msup|mtable|mtext|mrow|merror|' +
  'math|mtr|mtd|mo(?!v)|mi|mn|ms(?![qtpu])';

/**
 * Repairs a MathLive corruption (bug #191088, #191113, #191099): any keyboard edit to a raw-MathML
 * field — even just typing elsewhere in the content — drops the space between a tag name and its
 * first attribute in MathLive's internal model, e.g. '<mml:math xmlns:mml=' becomes
 * '<mml:mathxmlns:mml=', or '<mi mathvariant=' becomes '<mimathvariant=', producing invalid XML.
 * Not just the outer wrapper tag — any element in the string can lose its space this way. Anchored
 * to MathML's actual (small, stable, spec-defined) tag vocabulary rather than "any run of letters",
 * since the latter can't tell where a tag name ends and a glued attribute name begins (e.g. it would
 * split 'mimathvariant' as 'mimathvarian' + 't', not 'mi' + 'mathvariant'). Happens regardless of
 * getValue() mode, so it must be repaired after the fact rather than avoided.
 */
export function repairMathMlAttributeSpacing(value: string): string {
  return value.replace(new RegExp(`<(\\/?(?:mml:)?(?:${MATHML_TAG_ALTERNATION}))(?=[a-zA-Z])`, 'g'), '<$1 ');
}

/**
 * Pulls the literal text content back out of convertLatexToMathMl()'s output, which wraps it in a
 * single element (e.g. '<mi>&#x03c0;</mi>') rather than returning a bare character/entity. Returns
 * null for an empty/unrecognized result (convertLatexToMathMl() returns '' for a macro name it
 * doesn't know) so the caller can leave the original text untouched rather than guess.
 */
export function extractMathMlTextContent(mathMl: string): string | null {
  return mathMl.match(/>([^<]*)</)?.[1] ?? null;
}

/**
 * Repairs MathLive's other MathML round-trip corruption (bug #191104, #191118): on any genuine
 * edit, symbol tokens leak MathLive's internal LaTeX-macro representation into what should be raw
 * MathML — either a spurious backslash before an XML entity/numeric character reference
 * ('\&DifferentialD;', '\&#x207F;'), a doubled one before *both* the '&' and the following '#' of a
 * numeric reference ('\&\#x207F;' — bug #191118's actual repro, confirmed live: MathLive's
 * serializer sometimes escapes the '#' a second time on top of the '&'), or a literal Unicode
 * character replaced outright by its LaTeX macro name, still sitting inside the element
 * ('<mi>\pi</mi>' instead of '<mi>π</mi>'). The backslash is never valid XML in any of these
 * positions — pure noise MathLive's serializer adds. The macro-name case reuses MathLive's own
 * public convertLatexToMathMl() to resolve the correct character/entity rather than maintaining a
 * parallel macro-name-to-Unicode table ourselves.
 */
export function repairMathMlSymbolEscaping(value: string): string {
  const withoutStrayBackslash = value.replace(/\\(&)\\?(#x?[0-9A-Fa-f]+|[A-Za-z][A-Za-z0-9]*);/g, '$1$2;');

  return withoutStrayBackslash.replace(/\\([A-Za-z]+)/g, (match, macroName) => {
    const resolved = extractMathMlTextContent(convertLatexToMathMl('\\' + macroName));
    return resolved ?? match;
  });
}

/**
 * Runs both MathML corruption repairs (see their doc comments) in sequence — spacing first, since
 * it can put the string back into well-formed XML that the symbol-escaping step's entity-boundary
 * matching assumes, and the two corruptions are otherwise independent of each other.
 */
export function repairMathMlCorruption(value: string): string {
  return repairMathMlSymbolEscaping(repairMathMlAttributeSpacing(value));
}

/**
 * Renders the given equation string to an HTMLElement using MathJax. MathJax's dynamically-loaded
 * font (the newcm font's async chunks — see the asyncLoad override above) throws a "retry" signal
 * synchronously the first time a session renders a glyph it hasn't fetched yet; convertPromise()
 * awaits that fetch internally and retries, as many times in a row as needed (e.g. one retry per
 * not-yet-cached glyph in the equation), instead of surfacing it as an error. A genuine parse
 * failure still rejects the returned Promise.
 * @param equation The equation string to render.
 * @returns A Promise that resolves to the rendered equation as an HTMLElement.
 */
export function renderEquationAsync(equation: string): Promise<HTMLElement> {
  initMathJax();

  equation ??= '';

  if (equation.startsWith('<math') || equation.startsWith('<mml:')) {
    // Repairs already-saved documents whose MathML was corrupted before these fixes existed (or by
    // a corruption path buildSavedEquationNode()'s repair doesn't catch) — see
    // repairMathMlCorruption()'s doc comment.
    return mmlDocument.convertPromise(repairMathMlCorruption(equation), { display: false }) as Promise<HTMLElement>;
  } else {
    return texDocument.convertPromise(replacePlaceholders(equation), { display: false }) as Promise<HTMLElement>;
  }
}

/**
 * Builds the `madcapequation` node content to save an equation field's value back into the
 * document. If the value is raw MathML (e.g. pasted directly — MathLive doesn't understand
 * MathML, so it isn't converted to LaTeX), returns a `math`/`mmlmath` node preserving the raw
 * content so it round-trips through the schema's raw `toFlareXML` path instead of being escaped
 * like a plain text node would be. Otherwise, returns a text node wrapped in `$...$` LaTeX
 * delimiters, carrying forward `marks` (e.g. the `madcapchange` tracked-change mark) from the
 * original text child — otherwise a LaTeX equation edited under Track Changes (e.g. in Reviews,
 * which forces it on) silently drops its tracked-change mark on every save.
 * Note: repairMathMlCorruption() above fixes known instances of MathLive's raw-MathML round-trip
 * not being byte-for-byte (tag/attribute spacing, symbol/entity escaping); it does not guarantee
 * there are no other such mangling patterns still uncaught.
 * @param schema The schema to create the node from. Must have `math` and `mmlmath` node types registered.
 * @param value The equation field's saved value, without delimiters.
 * @param marks Marks to carry over onto the saved text node, when the value isn't raw MathML.
 */
export function buildSavedEquationNode(schema: Schema, value: string, marks?: readonly Mark[]): ProseMirrorNode {
  const repairedValue = repairMathMlCorruption(value);
  const match = repairedValue.match(wrappedMathRegex);
  if (match) {
    const mathTagName = match[1];
    const nodeTypeName = mathTagName.startsWith('mml:') ? 'mmlmath' : 'math';

    return schema.nodes[nodeTypeName].create({ content: repairedValue });
  }

  return schema.text('$' + value + '$', marks);
}

/**
 * Removes supported MathJax / LaTeX equation delimiters from the start and end
 * of an equation string.
 *
 * Supported delimiters:
 * - $$ ... $$   (display math)
 * - $ ... $     (inline math)
 * - \( ... \)   (inline MathJax / LaTeX)
 * - \[ ... \]   (display MathJax / LaTeX)
 *
 * Delimiters are only removed when a matching opening and closing pair exists.
 *
 * @param equation The equation string to process.
 * @returns The equation string without outer delimiters.
 */
export function removeEquationDelimiters(equation: string): string {
  if (!equation)
    return '';

  const value = equation.trim();

  // $$ ... $$   (display math)
  if (value.startsWith('$$') && value.endsWith('$$'))
    return value.slice(2, -2);

  // $ ... $     (inline math)
  if (value.startsWith('$') && value.endsWith('$'))
    return value.slice(1, -1);

  // \( ... \)   (inline MathJax / LaTeX)
  if (value.startsWith('\\(') && value.endsWith('\\)'))
    return value.slice(2, -2);

  // \[ ... \]   (display MathJax / LaTeX)
  if (value.startsWith('\\[') && value.endsWith('\\]'))
    return value.slice(2, -2);

  return value;
}
